import * as Cesium from 'cesium';

/**
 * "Zuhause festlegen" — ein fester Orientierungs-Marker (Haus-Pin) auf der
 * Karte, plus der Alarm-Zone: ein Ring (Radius aus homeState) um das Zuhause,
 * in dem tiefe Überflüge gemeldet werden (siehe ui/homeAlarm.js). Ablauf:
 *   1. Einstellungen → "Zuhause" aufklappen → "Setzen"
 *   2. auf die Karte tippen → Haus-Pin wird dort fixiert (in localStorage)
 *   3. Radius-Regler stellt die Alarm-Zone ein (1–3 km)
 *   4. "Löschen" entfernt Pin + Zone wieder
 * Pin und Zone bleiben aufs Gelände geklemmt sichtbar und überleben Reloads.
 */

// Minimalistischer Teardrop-Pin mit weißem Haus. Der weiße Rand sorgt für
// Kontrast auf hellen wie dunklen Basemaps; blau hebt ihn klar von den
// höhengefärbten Flügen ab.
const HOME_PIN = `data:image/svg+xml,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="64" height="64">` +
  `<path fill="#1a73e8" stroke="#ffffff" stroke-width="2.6" stroke-linejoin="round" ` +
  `d="M32 3C20.4 3 11 12.4 11 24c0 11.7 13.4 25.9 19.6 32a3.4 3.4 0 0 0 2.8 0C39.6 49.9 53 35.7 53 24 53 12.4 43.6 3 32 3Z"/>` +
  `<path fill="#ffffff" d="M32 13 46 25H18Z M23 24H41V35A1.5 1.5 0 0 1 39.5 36.5H24.5A1.5 1.5 0 0 1 23 35Z"/>` +
  `<path fill="#1a73e8" d="M29.6 30h4.8v6.5h-4.8z"/>` +
  `</svg>`
)}`;

// Alarm-Zone in Orange (passend zum Alarm-Button), dezent gehalten.
const ZONE_COLOR = '#f57c00';
const ZONE_FILL_ALPHA = 0.1;
const ZONE_RING_ALPHA = 0.7;

export function setupHomeMarker(viewer, homeState) {
  const toggle = document.getElementById('homeToggle');
  const panel = document.getElementById('homePanel');
  const setBtn = document.getElementById('homeSet');
  const clearBtn = document.getElementById('homeClear');
  const stateLabel = document.getElementById('homeToggleState');
  const radiusSlider = document.getElementById('homeRadius');
  const radiusValue = document.getElementById('homeRadiusValue');
  if (!toggle || !panel || !setBtn || !clearBtn) return;

  let homeEntity = null;
  let zoneFill = null;   // gefüllte Fläche der Alarm-Zone (aufs Gelände drapiert)
  let zoneRing = null;   // Umriss der Alarm-Zone (aufs Gelände geklemmt)
  let placing = false;
  let clickHandler = null;
  let hintEl = null;

  // --- Zone-Geometrie ------------------------------------------------------
  // Kreis-Positionen um das Zentrum, dicht gesampelt für einen glatten,
  // aufs Gelände geklemmten Ring.
  function ringPositions(lon, lat, radiusKm) {
    const r = radiusKm * 1000;
    const segments = 96;
    const latRad = (lat * Math.PI) / 180;
    const metersPerDegLat = 111320;
    const coords = [];
    for (let i = 0; i <= segments; i++) {
      const a = (i / segments) * 2 * Math.PI;
      const dLat = (r * Math.cos(a)) / metersPerDegLat;
      const dLon = (r * Math.sin(a)) / (metersPerDegLat * Math.cos(latRad));
      coords.push(lon + dLon, lat + dLat);
    }
    return Cesium.Cartesian3.fromDegreesArray(coords);
  }

  function renderZone(lon, lat, radiusKm) {
    const r = radiusKm * 1000;
    const center = Cesium.Cartesian3.fromDegrees(lon, lat);

    if (!zoneFill) {
      zoneFill = viewer.entities.add({
        id: 'home-zone-fill',
        position: center,
        ellipse: {
          semiMinorAxis: r,
          semiMajorAxis: r,
          material: Cesium.Color.fromCssColorString(ZONE_COLOR).withAlpha(ZONE_FILL_ALPHA),
          // Nur aufs Gelände drapieren (nicht auf 3D-Gebäude) — sonst zackige Ränder
          classificationType: Cesium.ClassificationType.TERRAIN
        }
      });
    } else {
      zoneFill.position = center;
      zoneFill.ellipse.semiMinorAxis = r;
      zoneFill.ellipse.semiMajorAxis = r;
    }

    if (!zoneRing) {
      zoneRing = viewer.entities.add({
        id: 'home-zone-ring',
        polyline: {
          positions: ringPositions(lon, lat, radiusKm),
          width: 2,
          clampToGround: true,
          material: Cesium.Color.fromCssColorString(ZONE_COLOR).withAlpha(ZONE_RING_ALPHA)
        }
      });
    } else {
      zoneRing.polyline.positions = ringPositions(lon, lat, radiusKm);
    }
  }

  function removeZone() {
    if (zoneFill) { viewer.entities.remove(zoneFill); zoneFill = null; }
    if (zoneRing) { viewer.entities.remove(zoneRing); zoneRing = null; }
  }

  // --- Pin + Zone anhand des geteilten Zustands zeichnen -------------------
  function render() {
    if (homeState.hasHome()) {
      const { lon, lat } = homeState.location;
      const pos = Cesium.Cartesian3.fromDegrees(lon, lat, 0);
      if (homeEntity) {
        homeEntity.position = pos;
      } else {
        homeEntity = viewer.entities.add({
          id: 'home-marker',
          position: pos,
          billboard: {
            image: HOME_PIN,
            width: 46,
            height: 46,
            // Pin-Spitze sitzt exakt auf dem gewählten Punkt
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            scaleByDistance: new Cesium.NearFarScalar(2.0e4, 1.0, 6.0e5, 0.6),
            // Fester Orientierungspunkt → immer sichtbar, auch hinter Gelände
            disableDepthTestDistance: Number.POSITIVE_INFINITY
          }
        });
      }
      renderZone(lon, lat, homeState.radiusKm);
    } else {
      if (homeEntity) { viewer.entities.remove(homeEntity); homeEntity = null; }
      removeZone();
    }
    reflect();
    viewer.scene.requestRender();
  }

  // --- Platzierungs-Modus (auf Karte tippen) -------------------------------
  function pickGround(windowPos) {
    const scene = viewer.scene;
    let cartesian = null;
    // Bevorzugt echte Gelände-/3D-Tiles-Höhe, sonst Ellipsoid
    if (scene.pickPositionSupported) {
      cartesian = scene.pickPosition(windowPos);
    }
    if (!Cesium.defined(cartesian)) {
      cartesian = viewer.camera.pickEllipsoid(windowPos, scene.globe.ellipsoid);
    }
    if (!Cesium.defined(cartesian)) return null;
    const carto = Cesium.Cartographic.fromCartesian(cartesian);
    return {
      lon: Cesium.Math.toDegrees(carto.longitude),
      lat: Cesium.Math.toDegrees(carto.latitude)
    };
  }

  function startPlacing() {
    if (placing) return;
    placing = true;
    setOpen(false);
    // Panel schließen, damit die ganze Karte antippbar ist
    document.getElementById('controlsPanel')?.classList.remove('open');
    viewer.scene.canvas.style.cursor = 'crosshair';
    showHint();

    clickHandler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    clickHandler.setInputAction((movement) => {
      const point = pickGround(movement.position);
      if (point) homeState.setLocation(point.lon, point.lat);
      // Eine evtl. gleichzeitig getroffene Flug-Auswahl wieder aufheben
      setTimeout(() => { viewer.selectedEntity = undefined; }, 0);
      stopPlacing();
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
  }

  function stopPlacing() {
    if (!placing) return;
    placing = false;
    viewer.scene.canvas.style.cursor = '';
    hideHint();
    if (clickHandler) {
      clickHandler.destroy();
      clickHandler = null;
    }
  }

  // --- Hinweis-Banner beim Platzieren --------------------------------------
  function showHint() {
    if (!hintEl) {
      hintEl = document.createElement('div');
      hintEl.className = 'glass home-hint';
      hintEl.innerHTML =
        '<span>Tippe auf die Karte, um dein Zuhause zu setzen</span>' +
        '<button type="button" class="home-hint-cancel">Abbrechen</button>';
      hintEl.querySelector('.home-hint-cancel').addEventListener('click', stopPlacing);
      document.body.appendChild(hintEl);
    }
    hintEl.classList.remove('hidden');
  }

  function hideHint() {
    if (hintEl) hintEl.classList.add('hidden');
  }

  // --- UI-Zustand ----------------------------------------------------------
  function formatRadius(km) {
    return km.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  }

  function reflect() {
    const has = homeState.hasHome();
    if (stateLabel) stateLabel.textContent = has ? 'Gesetzt' : 'Nicht gesetzt';
    clearBtn.disabled = !has;
    setBtn.textContent = has ? 'Zuhause verschieben' : 'Zuhause setzen';
    if (radiusValue) radiusValue.textContent = formatRadius(homeState.radiusKm);
  }

  function setOpen(open) {
    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    toggle.classList.toggle('open', open);
  }

  // --- Verdrahtung ---------------------------------------------------------
  toggle.addEventListener('click', () => {
    setOpen(toggle.getAttribute('aria-expanded') !== 'true');
  });
  setBtn.addEventListener('click', startPlacing);
  clearBtn.addEventListener('click', () => homeState.clearLocation());

  if (radiusSlider) {
    radiusSlider.value = String(homeState.radiusKm);
    radiusSlider.addEventListener('input', () => {
      homeState.setRadiusKm(Number(radiusSlider.value));
    });
  }

  // Escape bricht das Platzieren ab (vor der globalen Deselect-Logik)
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && placing) {
      e.stopPropagation();
      stopPlacing();
    }
  }, true);

  // Auf Zustandsänderungen reagieren (auch aus dem Radius-Regler) und
  // gespeichertes Zuhause direkt beim Start wiederherstellen.
  homeState.on('change', render);
  render();
}
